import { MNSproCloudApi } from '..';
import {
  B2CUser,
  CreateGuardianContactBody,
  GuardianContact,
  GuardianContactCreateMailMergeBody,
  MailMergeSortBy,
  MailMergeSortDirection,
  StudentWithGuardianContacts,
  StudentWithPrimaryGuardianContact,
  UpdateGuardianContactBody,
  WardConnectionDetail,
} from '../api/v2';

export interface PcGuardianContactCreateMergeMailOptions {
  oneTimeUse?: boolean;
  validFor?: string;
  createSingleFiles?: boolean;
  sort?: MailMergeSortBy;
  sortDirection?: MailMergeSortDirection;
}
export class ParentalCommunicationGuardiansService {
  private constructor() {}

  private static getApi(authToken: string) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ParentalCommunicationApiFactory(config);
    return api;
  }

  public static async getAll(
    authToken: string,
    organizationId: string,
  ): Promise<StudentWithPrimaryGuardianContact[]> {
    const api = ParentalCommunicationGuardiansService.getApi(authToken);
    const response = await api.getAllGuardianContacts(organizationId);
    return response.data;
  }

  public static async get(
    authToken: string,
    organizationId: string,
    studentId: string,
  ): Promise<StudentWithGuardianContacts> {
    const api = ParentalCommunicationGuardiansService.getApi(authToken);
    const response = await api.getGuardianContactsForStudent(organizationId, studentId);
    return response.data;
  }

  public static async getWardConnectionsFor(
    authToken: string,
    organizationId: string,
    studentId: string,
  ): Promise<WardConnectionDetail[]> {
    const api = ParentalCommunicationGuardiansService.getApi(authToken);
    const response = await api.getWardConnectionsFor(organizationId, studentId);
    return response.data.connections ?? [];
  }

  public static async updateGuardianContact(
    authToken: string,
    organizationId: string,
    contactId: string,
    wardId: string,
    contact: UpdateGuardianContactBody,
  ): Promise<GuardianContact> {
    const api = ParentalCommunicationGuardiansService.getApi(authToken);

    const response = await api.updateGuardianContact(organizationId, contactId, contact);

    if (contact.isPrimaryContact) {
      await api.setPrimaryContactForWard(organizationId, {
        wardId: wardId,
        guardianContactId: contactId,
      });
    }

    return response.data;
  }

  /**
   * Creates a guardian contact for the given student. When `contact.isPrimaryContact`
   * is set, additionally calls `setPrimaryContactForWard` to make the new contact the
   * ward's primary contact (which also demotes the previous primary server-side) —
   * mirroring `updateGuardianContact`, since the backend create does not persist the flag.
   */
  public static async createGuardianContact(
    authToken: string,
    organizationId: string,
    contact: CreateGuardianContactBody,
  ): Promise<GuardianContact> {
    const api = ParentalCommunicationGuardiansService.getApi(authToken);
    const response = await api.createGuardianContact(organizationId, contact);

    if (contact.isPrimaryContact && response.data.id) {
      await api.setPrimaryContactForWard(organizationId, {
        wardId: contact.studentId,
        guardianContactId: response.data.id,
      });
    }

    return response.data;
  }

  public static async deleteGuardianContact(
    authToken: string,
    organizationId: string,
    contactId: string,
  ): Promise<void> {
    const api = ParentalCommunicationGuardiansService.getApi(authToken);
    await api.deleteGuardianContact(organizationId, contactId);
  }

  /**
   * @param authToken
   * @param organizationId
   * @param studentIds
   * @param oneTimeUse
   * @returns Job id
   */
  public static async createMergeMail(
    authToken: string,
    organizationId: string,
    studentIds: string[],
    options: PcGuardianContactCreateMergeMailOptions = {},
  ): Promise<string> {
    const api = ParentalCommunicationGuardiansService.getApi(authToken);
    const body: GuardianContactCreateMailMergeBody = {
      studentIds,
      oneTimeUse: options.oneTimeUse ?? false,
      validFor: options.validFor ?? null,
      createSingleFiles: options.createSingleFiles ?? false,
      sort: options.sort ?? MailMergeSortBy.lastName,
      sortDirection: options.sortDirection ?? MailMergeSortDirection.ascending,
    };
    const response = await api.createMailMerge(organizationId, body);

    return response.data;
  }

  public static async getMergeMailDownloadLink(
    authToken: string,
    organizationId: string,
    jobId: string,
  ): Promise<string | null> {
    const api = ParentalCommunicationGuardiansService.getApi(authToken);
    const response = await api.getDownloadLinkForMailMerge(organizationId, jobId);

    return response.data.downloadLink ?? null;
  }

  /**
   * @deprecated
   */
  public static async getParentsB2CUsersForStudent(
    authToken: string,
    organizationId: string,
    studentId: string,
  ): Promise<B2CUser[]> {
    const api = ParentalCommunicationGuardiansService.getApi(authToken);
    const response = await api.getB2CUserFor(organizationId, studentId);
    return response.data;
  }

  public static async deleteGuardianConnection(
    authToken: string,
    organizationId: string,
    studentId: string,
    parentMail: string,
  ) {
    const api = ParentalCommunicationGuardiansService.getApi(authToken);
    await api.deleteGuardianConnection(organizationId, parentMail, studentId);
  }

  public static async revokeAllInvitations(
    authToken: string,
    organizationId: string,
    studentId: string,
  ) {
    const api = ParentalCommunicationGuardiansService.getApi(authToken);
    await api.revokeAllInvitationsForWard(organizationId, studentId);
  }
}
